<template>
  <div class="h-full">
    <div class="w-full flex items-center justify-between select-none mt-1 mr-4 mb-0.5" style="height: 52px">
      <div class="flex items-center">
        <EditorDropDownMenu :titleIcon="'menu'" />
        <OutlinedIconTextButton
          id="reviewButton"
          :border_radius="2"
          class="mx-1"
          :disabled="judgeStatePresenter.isTestLoading.value || judgeStatePresenter.isJudgeLoading.value"
          :font_weight="'700'"
          :icon="'reviews'"
          :iconSize="'text-xl'"
          :padding_bottom="4"
          :padding_left="20"
          :padding_right="20"
          :padding_top="4"
          :text="$t('message.review')"
          :textSize="'text-base'"
          @click="clickReview"
        />
      </div>
      <div class="w-full flex items-center justify-end" style="user-select: none; margin: 4px 16px 2px 0px">
        <OutlinedIconTextButton
          id="testButton"
          :border_radius="2"
          class="mx-1"
          :disabled="judgeStatePresenter.isTestLoading.value || judgeStatePresenter.isJudgeLoading.value"
          :font_weight="'700'"
          :icon="'check'"
          :iconSize="'text-xl'"
          :loading="judgeStatePresenter.isTestLoading.value"
          :padding_bottom="4"
          :padding_left="20"
          :padding_right="20"
          :padding_top="4"
          :text="$t('message.run')"
          :textSize="'text-base'"
          @click="clickTestSubmit"
        />
        <IconTextButton
          id="submitButton"
          :border_radius="2"
          class="mx-1"
          :disabled="judgeStatePresenter.isTestLoading.value || judgeStatePresenter.isJudgeLoading.value"
          :font_weight="'700'"
          :icon="'check_circle'"
          :iconSize="'text-xl'"
          :loading="judgeStatePresenter.isJudgeLoading.value"
          :padding_bottom="4"
          :padding_left="20"
          :padding_right="20"
          :padding_top="4"
          :text="$t('message.submit')"
          :textSize="'text-base'"
          @click="clickSubmit"
        />
      </div>
    </div>
    <div class="border-b border-gray-300 dark:border-dark-400" />
    <div
      class="overflow-y-auto py-2 px-2"
      :class="{
        'custom-scrollbar': isLightMode(),
        'custom-scrollbar-dark': !isLightMode()
      }"
      :style="`height: ${editorPageStatePresenter.testFrameHeight.value - 60}px`"
    >
      <div>
        <div
          v-if="
            editorPageStatePresenter.isErrorShow.value &&
            !judgeStatePresenter.isTestLoading.value &&
            !judgeStatePresenter.isJudgeLoading.value &&
            ((testJudgeDetail && editorPageStatePresenter.errorMessage.value) ||
              (judgeDetail && editorPageStatePresenter.errorMessage.value))
          "
          class="w-full flex"
        >
          <ErrorText
            class="shadow-md"
            :close="() => editorPageStatePresenter.setIsErrorShow(false)"
            :text="editorPageStatePresenter.errorMessage.value"
          />
        </div>
        <div class="w-full flex" style="user-select: none">
          <div
            class="w-full grid gap-2"
            :class="!editorPageStatePresenter.isFreeSpaceMode.value ? 'grid-cols-3' : 'grid-cols-2'"
            :style="`height: ${editorPageStatePresenter.testPanelHeight.value + 32}px`"
          >
            <div>
              <MiniPanel
                id="input"
                :class="editorPageStatePresenter.isPastingIOConsole.value ? 'flash' : ''"
                :icon="'keyboard_arrow_right'"
                :title="$t('message.input')"
              >
                <template #header>
                  <DropDownMenu :itemList="pasteTestCaseList" :selected="setTestCase">
                    <template #title>
                      <div class="text-center">
                        <span
                          :class="'material-icons bg-red-100 text-white dark:text-dark-text-dark'"
                          style="font-size: 1rem; line-height: 0"
                        >
                          arrow_drop_down
                        </span>
                      </div>
                    </template>
                  </DropDownMenu>
                </template>
                <template #body>
                  <div class="p-2 pb-3">
                    <MonacoEditorMini
                      :change="judgeStatePresenter.setTestInput"
                      class="border dark:border-dark-400"
                      :isEditable="true"
                      :value="judgeStatePresenter.testInput.value"
                    />
                  </div>
                </template>
              </MiniPanel>
            </div>
            <div v-if="!editorPageStatePresenter.isFreeSpaceMode.value" id="expect-output">
              <MiniPanel
                :class="editorPageStatePresenter.isPastingIOConsole.value ? 'flash' : ''"
                :icon="'keyboard_double_arrow_left'"
                :title="$t('message.expectedOutput')"
              >
                <template #body>
                  <div class="p-2 pb-3">
                    <MonacoEditorMini
                      :change="judgeStatePresenter.setTestOutput"
                      class="border dark:border-dark-400"
                      :isEditable="true"
                      :value="judgeStatePresenter.testOutput.value"
                    />
                  </div>
                </template>
              </MiniPanel>
            </div>
            <div v-if="!editorPageStatePresenter.isFreeSpaceMode.value" id="output">
              <MiniPanel
                :headerColor="userOutputHeaderColor"
                :icon="'keyboard_arrow_left'"
                :title="$t('message.output')"
              >
                <template #header>
                  <div
                    class="material-icons flex items-center justify-center"
                    :class="viewMode == 'LIGHT' ? 'hover-text-icon' : 'hover-text-icon-dark'"
                    :data-tooltip="$t('message.resultOf') + ' ' + $t('message.test') + '' + $t('message.kekka')"
                  >
                    <JudgeBadgeMini v-if="judgeStatePresenter.isTestLoading.value" :status="''" />
                    <JudgeBadgeMini v-else-if="testResultStatus" :status="testResultStatus" />
                    <JudgeBadgeMini v-else :status="''" />
                  </div>
                </template>
                <template #body>
                  <p
                    v-if="judgeStatePresenter.isTestLoading.value"
                    class="py-2 px-4 bg-gray-200 dark:bg-dark-800"
                    style="border-radius: 0 0 1rem 1rem"
                    :style="`height: ${editorPageStatePresenter.testPanelHeight.value}px`"
                  />
                  <div
                    v-else-if="testResultStatus === 'WRONGANSWER' || testResultStatus === 'PRESENTATIONERROR'"
                    class="p-2 pb-3"
                    style="border-radius: 0 0 1rem 1rem"
                  >
                    <MonacoEditorMini
                      :change="judgeStatePresenter.setTestOutput"
                      class="border dark:border-dark-400"
                      :isEditable="false"
                      :value="userOutput"
                    />
                  </div>
                  <div
                    v-else-if="testResultStatus === 'ACCEPTED'"
                    class="p-2 pb-3"
                    style="border-radius: 0 0 1rem 1rem"
                  >
                    <MonacoEditorMini
                      :change="judgeStatePresenter.setTestOutput"
                      class="border dark:border-dark-400"
                      :isEditable="false"
                      :value="userOutput"
                    />
                  </div>
                  <p
                    v-else
                    class="py-2 px-4 bg-gray-200 dark:bg-dark-800"
                    style="border-radius: 0 0 1rem 1rem"
                    :style="`height: ${editorPageStatePresenter.testPanelHeight.value}px`"
                  />
                </template>
              </MiniPanel>
            </div>
            <div v-else id="output" class="w-full text-sm">
              <MiniPanel :icon="'keyboard_arrow_left'" :title="$t('message.output')">
                <template #body>
                  <p
                    v-if="judgeStatePresenter.isTestLoading.value"
                    class="py-2 px-4 bg-gray-100 dark:bg-dark-800"
                    style="border-radius: 0 0 1rem 1rem"
                    :style="`height: ${editorPageStatePresenter.testPanelHeight.value}px`"
                  />
                  <div
                    v-else-if="
                      testResultStatus === 'ACCEPTED' ||
                      testResultStatus === 'WRONGANSWER' ||
                      testResultStatus === 'PRESENTATIONERROR'
                    "
                    class="p-2 pb-3"
                    style="border-radius: 0 0 1rem 1rem"
                  >
                    <MonacoEditorMini
                      :change="judgeStatePresenter.setTestOutput"
                      class="border dark:border-dark-400"
                      :isEditable="false"
                      :value="userOutput"
                    />
                  </div>
                  <p
                    v-else
                    class="py-2 px-4 bg-gray-100 dark:bg-dark-800"
                    style="border-radius: 0 0 1rem 1rem"
                    :style="`height: ${editorPageStatePresenter.testPanelHeight.value}px`"
                  />
                </template>
              </MiniPanel>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue'

import IconTextButton from '@presentation/components/Button/IconTextButton.vue'
import OutlinedIconTextButton from '@presentation/components/Button/OutlinedIconTextButton.vue'
import DropDownMenu from '@presentation/components/DropDown/DropDownMenu.vue'
import MonacoEditorMini from '@presentation/components/MonacoEditor/MonacoEditorMini.vue'
import MiniPanel from '@presentation/components/Panel/MiniPanel.vue'
import ErrorText from '@presentation/components/Text/ErrorText.vue'
import JudgeBadgeMini from '@presentation/components/Text/JudgeBadgeMini.vue'

import EditorDropDownMenu from './EditorDropDownMenu.vue'

import { JudgeStatusType } from '@/domain/value_objects/judge/JudgeStatusType'
import { useArenaPresenter } from '@/presentation/composables/arena/useArenaPresenter'
import { usePostArenaSubmissionPresenter } from '@/presentation/composables/arena/usePostArenaSubmissionPresenter'
import { useAuthStatePresenter } from '@/presentation/composables/auth/useAuthStatePresenter'
import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useEditorPageStatePresenter } from '@/presentation/composables/editor/useEditorPageStatePresenter'
import { useJudgeDetailPresenter } from '@/presentation/composables/judge/useJudgeDetailPresenter'
import { useJudgeStatePresenter } from '@/presentation/composables/judge/useJudgeStatePresenter'
import { usePostSubmissionPresenter } from '@/presentation/composables/judge/usePostSubmissionPresenter'
import { usePostTestSubmissionPresenter } from '@/presentation/composables/judge/usePostTestSubmissionPresenter'
import { useNotificationPresenter } from '@/presentation/composables/notification/useNotificationPresenter'
import { useProblemStatePresenter } from '@/presentation/composables/problem/useProblemStatePresenter'
import { useTestCasePresenter } from '@/presentation/composables/testCase/useTestCasePresenter'

export default defineComponent({
  components: {
    IconTextButton,
    OutlinedIconTextButton,
    EditorDropDownMenu,
    MiniPanel,
    MonacoEditorMini,
    JudgeBadgeMini,
    DropDownMenu,
    ErrorText
  },
  setup(props, ctx) {
    const editorPageStatePresenter = useEditorPageStatePresenter()
    const notificationPresenter = useNotificationPresenter()
    const judgeStatePresenter = useJudgeStatePresenter()
    const { isLogin } = useAuthStatePresenter()
    const { programLanguage, displayLanguage, viewMode, isLightMode } = useConfigStatePresenter()
    const { problemId } = useProblemStatePresenter()
    const { postSubmission } = usePostSubmissionPresenter()
    const { postTestSubmission } = usePostTestSubmissionPresenter()
    const { postArenaSubmission } = usePostArenaSubmissionPresenter()
    const { testCaseList } = useTestCasePresenter()
    const { testJudgeDetail, judgeDetail } = useJudgeDetailPresenter()
    const arenaPresenter = useArenaPresenter()

    const openReviewAlertModal = (judgeId: number, status: JudgeStatusType) => {
      ctx.emit('openReviewAlertModal', judgeId, status)
    }

    const clickReview = () => {
      if (judgeDetail.value != undefined) {
        openReviewAlertModal(judgeDetail.value.judgeId, judgeDetail.value.submissionRecord.status)
      } else {
        notificationPresenter.invalidReviewNotification()
      }
    }

    const clickSubmit = () => {
      if (isLogin.value) {
        if (arenaPresenter.arenaId.value != '') {
          postArenaSubmission(
            problemId.value,
            arenaPresenter.arenaProblemId.value,
            arenaPresenter.arenaId.value,
            programLanguage.value,
            editorPageStatePresenter.sourceCode.value
          )
        } else {
          postSubmission(problemId.value, programLanguage.value, editorPageStatePresenter.sourceCode.value)
        }
      } else {
        notificationPresenter.noLoginNotification()
      }
    }

    const clickTestSubmit = () => {
      if (isLogin.value) {
        if (editorPageStatePresenter.isFreeSpaceMode.value) {
          postTestSubmission(
            problemId.value,
            programLanguage.value,
            editorPageStatePresenter.sourceCode.value,
            'freespace'
          )
        } else {
          postTestSubmission(
            problemId.value,
            programLanguage.value,
            editorPageStatePresenter.sourceCode.value,
            'default'
          )
        }
      } else {
        notificationPresenter.noLoginNotification()
      }
    }

    const errorMessageElement = ref<HTMLDivElement>()

    const testResultStatus = computed(() =>
      testJudgeDetail.value
        ? testJudgeDetail.value.submissionRecord
          ? testJudgeDetail.value.submissionRecord.status
          : ''
        : ''
    )
    const userOutput = computed(() => (testJudgeDetail.value ? testJudgeDetail.value.userOutput : ''))
    const pasteTestCaseList = computed(() =>
      testCaseList.value.map((value, index) =>
        displayLanguage.value == 'Japanese' ? `サンプル${index + 1}を貼り付け` : `Paste sample case ${index + 1}`
      )
    )

    const setTestCase = (index: number, value: string) => {
      judgeStatePresenter.setTestInput(testCaseList.value[index].in)
      judgeStatePresenter.setTestOutput(testCaseList.value[index].out)
    }

    const userOutputHeaderColor = computed(() => {
      if (judgeStatePresenter.isTestLoading.value) {
        return 'bg-primary-500 dark:bg-dark-400'
      } else if (testResultStatus.value === 'ACCEPTED') {
        return 'bg-teal-400 dark:bg-teal-800'
      } else if (testResultStatus.value === 'WRONGANSWER' || testResultStatus.value === 'PRESENTATIONERROR') {
        return 'bg-red-300 dark:bg-red-900'
      } else {
        return 'bg-primary-500 dark:bg-dark-400'
      }
    })

    return {
      clickSubmit,
      clickTestSubmit,
      clickReview,
      openReviewAlertModal,
      isLightMode,
      viewMode,
      testResultStatus,
      testCaseList,
      pasteTestCaseList,
      userOutput,
      judgeStatePresenter,
      editorPageStatePresenter,
      setTestCase,
      testJudgeDetail,
      judgeDetail,
      errorMessageElement,
      userOutputHeaderColor
    }
  }
})
</script>

<style lang="postcss" scoped>
.flash {
  animation: flash 1s linear;
  animation-iteration-count: 2;
}
@keyframes flash {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
    background: #5b5f72;
  }
}

.hover-text-icon {
  text-decoration: none;

  &:hover {
    @apply relative;

    &::before {
      content: '';
      left: -13px;
      border-bottom: 8px solid transparent;
      border-top: 8px solid transparent;
      border-left: 8px solid #232a3b;
      @apply block absolute;
    }

    &::after {
      font-family: sans-serif;
      content: attr(data-tooltip);
      right: 140%;
      @apply bg-aoj text-white rounded p-2 block absolute font-bold text-sm pointer-events-none z-10;
    }
  }
}

.hover-text-icon-dark {
  text-decoration: none;

  &:hover {
    @apply relative;

    &::before {
      content: '';
      left: -13px;
      border-bottom: 8px solid transparent;
      border-top: 8px solid transparent;
      border-left: 8px solid white;
      @apply block absolute;
    }

    &::after {
      font-family: sans-serif;
      content: attr(data-tooltip);
      right: 140%;
      @apply bg-black border text-dark-text rounded p-2 block absolute font-bold text-sm pointer-events-none z-10;
    }
  }
}
</style>
